import { useState, useEffect, useCallback } from 'react';
import type {
  DistributionSuggestion,
  Transfer,
} from '@/types/location.types';
import { authFetch } from '@/services/api.service';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

// Interface for transfer filters
export interface TransferFilters {
  dateFrom: string;
  dateTo: string;
  locationId: string;
  status: string;
}

const getWeekRange = () => {
  const now = new Date();
  const day = now.getDay(); // 0=domingo, 1=lunes...
  const diffToMonday = day === 0 ? -6 : 1 - day;
  const monday = new Date(now);
  monday.setDate(now.getDate() + diffToMonday);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);

  const fmt = (d: Date) => d.toISOString().split('T')[0];
  return { dateFrom: fmt(monday), dateTo: fmt(sunday) };
};

const defaultFilters: TransferFilters = {
  ...getWeekRange(),
  locationId: '',
  status: '',
};

// Interface for API transfer response
interface ApiTransfer {
  id: number;
  transferNumber: string;
  fromLocationId: number;
  fromLocationName: string;
  toLocationId: number;
  toLocationName: string;
  status: 'pending' | 'approved' | 'in_transit' | 'received' | 'cancelled';
  type: 'distribution' | 'request' | 'transfer' | 'return';
  notes?: string;
  requestedBy?: string;
  approvedBy?: string;
  shippedBy?: string;
  receivedBy?: string;
  requestedAt?: string;
  approvedAt?: string;
  shippedAt?: string;
  receivedAt?: string;
  estimatedDelivery?: string;
  actualDelivery?: string;
  createdAt: string;
  itemsCount: number;
  items: Array<{
    id: number;
    ingredientId: number;
    ingredientName: string;
    unit: string;
    requestedQuantity: number;
    confirmedQuantity?: number;
    receivedQuantity?: number;
    reason?: string;
  }>;
}

// Map API response to frontend Transfer type
const mapApiTransferToTransfer = (apiTransfer: ApiTransfer): Transfer => ({
  id: String(apiTransfer.id),
  transferNumber: apiTransfer.transferNumber,
  fromLocationId: String(apiTransfer.fromLocationId),
  fromLocation: {
    id: String(apiTransfer.fromLocationId),
    name: apiTransfer.fromLocationName,
    type: 'central',
    address: '',
    isActive: true,
    timezone: 'America/Lima',
    createdAt: '',
    updatedAt: ''
  },
  toLocationId: String(apiTransfer.toLocationId),
  toLocation: {
    id: String(apiTransfer.toLocationId),
    name: apiTransfer.toLocationName,
    type: 'sucursal',
    address: '',
    isActive: true,
    timezone: 'America/Lima',
    createdAt: '',
    updatedAt: ''
  },
  status: apiTransfer.status === 'approved' ? 'pending' : apiTransfer.status,
  type: apiTransfer.type,
  items: apiTransfer.items.map(item => ({
    id: String(item.id),
    transferId: String(apiTransfer.id),
    ingredientId: String(item.ingredientId),
    ingredient: {
      id: String(item.ingredientId),
      name: item.ingredientName,
      unit: item.unit,
      category: '',
      currentStock: 0,
      minStock: 0,
      maxStock: 0,
      costPerUnit: 0,
      supplierId: '',
      isActive: true,
      createdAt: '',
      updatedAt: ''
    },
    requestedQuantity: item.requestedQuantity,
    confirmedQuantity: item.confirmedQuantity,
    receivedQuantity: item.receivedQuantity,
    unit: item.unit,
    reason: item.reason
  })),
  notes: apiTransfer.notes,
  requestedBy: apiTransfer.requestedBy || '',
  approvedBy: apiTransfer.approvedBy,
  shippedBy: apiTransfer.shippedBy,
  receivedBy: apiTransfer.receivedBy,
  requestedAt: apiTransfer.requestedAt || apiTransfer.createdAt,
  approvedAt: apiTransfer.approvedAt,
  shippedAt: apiTransfer.shippedAt,
  receivedAt: apiTransfer.receivedAt,
  estimatedDelivery: apiTransfer.estimatedDelivery,
  actualDelivery: apiTransfer.actualDelivery
});

export const useDistribution = (options?: { isCajero?: boolean; locationId?: string }) => {
  const [suggestions, setSuggestions] = useState<DistributionSuggestion[]>([]);
  const [transfers, setTransfers] = useState<Transfer[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [transferFilters, setTransferFilters] = useState<TransferFilters>(defaultFilters);

  // Fetch suggestions from API
  const fetchSuggestions = useCallback(async (locationId?: string) => {
    try {
      const params = locationId ? `?locationId=${locationId}` : '';
      const response = await authFetch(`${API_BASE_URL}/distribution/suggestions${params}`);

      if (!response.ok) {
        throw new Error('Error fetching suggestions');
      }

      const result = await response.json();
      if (result.success && result.data) {
        return result.data as DistributionSuggestion[];
      }
      return [];
    } catch (err) {
      console.error('Error fetching suggestions:', err);
      throw err;
    }
  }, []);

  // Fetch transfers from API
  const fetchTransfers = useCallback(async (filters?: TransferFilters) => {
    try {
      const params = new URLSearchParams();

      // Si hay filtros explícitos, usarlos; sino usar default (todos los estados activos)
      const f = filters || defaultFilters;

      // Para CAJERO: solo in_transit, sin fechas, filtrar por su local
      if (options?.isCajero) {
        params.append('status', 'in_transit');
        if (options.locationId) params.append('toLocationId', options.locationId);
      } else {
        if (f.status) {
          params.append('status', f.status);
        } else {
          params.append('status', 'pending,approved,in_transit,received');
        }

        if (f.dateFrom) params.append('dateFrom', f.dateFrom);
        if (f.dateTo) params.append('dateTo', f.dateTo);
        if (f.locationId) params.append('toLocationId', f.locationId);
      }

      const response = await authFetch(`${API_BASE_URL}/transfers?${params.toString()}`);

      if (!response.ok) {
        throw new Error('Error fetching transfers');
      }

      const result = await response.json();
      if (result.success && result.data) {
        return result.data.map(mapApiTransferToTransfer);
      }
      return [];
    } catch (err) {
      console.error('Error fetching transfers:', err);
      throw err;
    }
  }, []);

  // Load data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch suggestions and transfers in parallel
      const [suggestionsData, transfersData] = await Promise.all([
        fetchSuggestions(),
        fetchTransfers()
      ]);

      setSuggestions(suggestionsData);
      setTransfers(transfersData);

    } catch (err) {
      setError('Error al cargar datos de distribución');
      console.error('Distribution data loading error:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchSuggestions, fetchTransfers]);

  // Cargar datos iniciales
  useEffect(() => {
    loadData().finally(() => setInitialLoading(false));
  }, [loadData]);

  // Refresh data (bajo demanda)
  const refreshData = useCallback(async () => {
    await loadData();
  }, [loadData]);

  // Update transfer filters
  const updateTransferFilters = useCallback((updates: Partial<TransferFilters>) => {
    setTransferFilters(prev => ({ ...prev, ...updates }));
  }, []);

  // Search transfers with current filters (accepts optional overrides)
  const searchTransfers = useCallback(async (overrides?: Partial<TransferFilters>) => {
    try {
      setLoading(true);
      setError(null);
      const f = { ...transferFilters, ...overrides };
      if (overrides) {
        setTransferFilters(f);
      }
      const transfersData = await fetchTransfers(f);
      setTransfers(transfersData);
    } catch (err) {
      setError('Error al buscar transferencias');
    } finally {
      setLoading(false);
    }
  }, [fetchTransfers, transferFilters]);

  // Generar nuevas sugerencias (bajo demanda)
  const generateSuggestions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const suggestionsData = await fetchSuggestions();
      setSuggestions(suggestionsData);

    } catch (err) {
      setError('Error al generar sugerencias');
    } finally {
      setLoading(false);
    }
  }, [fetchSuggestions]);

  // Crear orden de distribución - agrupa por locationId y crea transferencias reales
  const createDistributionOrder = useCallback(async (selectedSuggestions: DistributionSuggestion[]) => {
    try {
      setLoading(true);

      // Agrupar sugerencias por locationId
      const grouped: Record<string, DistributionSuggestion[]> = {};
      for (const sug of selectedSuggestions) {
        if (!grouped[sug.locationId]) {
          grouped[sug.locationId] = [];
        }
        grouped[sug.locationId].push(sug);
      }

      // Encontrar el locationId del local central desde las sugerencias
      const firstSuggestion = selectedSuggestions[0] as any;
      const centralLocationId = firstSuggestion?.centralLocationId;

      // Si no tenemos centralLocationId, buscar el local PRINCIPAL
      let fromLocationId = centralLocationId;
      if (!fromLocationId) {
        try {
          const locResponse = await authFetch(`${API_BASE_URL}/locations`);
          if (locResponse.ok) {
            const locResult = await locResponse.json();
            const locations = locResult.data || locResult;
            const principal = (Array.isArray(locations) ? locations : []).find(
              (l: any) => l.tipoLocal === 'PRINCIPAL' && l.estado === 'ACTIVO'
            );
            if (principal) fromLocationId = principal.id;
          }
        } catch {
          // Fallback: usar 1 como local principal
          fromLocationId = 1;
        }
      }

      // Crear una transferencia por cada local destino
      const results = [];
      for (const [toLocationId, sugs] of Object.entries(grouped)) {
        const response = await authFetch(`${API_BASE_URL}/transfers`, {
          method: 'POST',
          body: JSON.stringify({
            fromLocationId: parseInt(String(fromLocationId)),
            toLocationId: parseInt(toLocationId),
            type: 'distribution',
            notes: 'Orden de distribución generada desde sugerencias',
            items: sugs.map(s => ({
              ingredientId: parseInt(s.ingredientId),
              requestedQuantity: s.suggestedQuantity,
              reason: s.reason
            }))
          })
        });

        if (!response.ok) {
          const errData = await response.json();
          throw new Error(errData.error || 'Error al crear transferencia');
        }

        const result = await response.json();
        results.push(result.data);
      }

      // Remover sugerencias que se convirtieron en órdenes
      setSuggestions(prev => prev.filter(
        suggestion => !selectedSuggestions.some(selected => selected.id === suggestion.id)
      ));

      // Refrescar transferencias
      const transfersData = await fetchTransfers();
      setTransfers(transfersData);

      return { success: true, results };

    } catch (err: any) {
      setError('Error al crear orden de distribución');
      return { success: false, error: err.message || 'Error al crear orden de distribución' };
    } finally {
      setLoading(false);
    }
  }, [fetchTransfers]);

  // Aprobar transferencia
  const approveTransfer = useCallback(async (transferId: string) => {
    try {
      setLoading(true);

      const response = await authFetch(`${API_BASE_URL}/transfers/${transferId}`, {
        method: 'PUT',
        body: JSON.stringify({ status: 'approved' })
      });

      if (!response.ok) {
        throw new Error('Error al aprobar transferencia');
      }

      const transfersData = await fetchTransfers();
      setTransfers(transfersData);

      return { success: true };

    } catch (err) {
      setError('Error al aprobar transferencia');
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, [fetchTransfers]);

  // Marcar transferencia como enviada
  const shipTransfer = useCallback(async (transferId: string) => {
    try {
      setLoading(true);

      const response = await authFetch(`${API_BASE_URL}/transfers/${transferId}`, {
        method: 'PUT',
        body: JSON.stringify({ status: 'in_transit' })
      });

      if (!response.ok) {
        throw new Error('Error al enviar transferencia');
      }

      const transfersData = await fetchTransfers();
      setTransfers(transfersData);

      return { success: true };

    } catch (err) {
      setError('Error al enviar transferencia');
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, [fetchTransfers]);

  // Marcar transferencia como recibida
  const receiveTransfer = useCallback(async (transferId: string, receivedItems: any[]) => {
    try {
      setLoading(true);

      const response = await authFetch(`${API_BASE_URL}/transfers/${transferId}`, {
        method: 'PUT',
        body: JSON.stringify({
          status: 'received',
          receivedItems: receivedItems.map(item => ({
            id: item.id,
            receivedQuantity: item.receivedQuantity
          }))
        })
      });

      if (!response.ok) {
        throw new Error('Error al recibir transferencia');
      }

      // Refresh transfers list
      const transfersData = await fetchTransfers();
      setTransfers(transfersData);

      return { success: true };

    } catch (err) {
      setError('Error al recibir transferencia');
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, [fetchTransfers]);

  // Crear transferencia manual
  const createManualTransfer = useCallback(async (transferData: any) => {
    try {
      const response = await authFetch(`${API_BASE_URL}/transfers`, {
        method: 'POST',
        body: JSON.stringify({
          fromLocationId: parseInt(transferData.fromLocationId),
          toLocationId: parseInt(transferData.toLocationId),
          type: transferData.type || 'transfer',
          notes: transferData.notes,
          items: transferData.items.map((item: any) => ({
            ingredientId: parseInt(item.ingredientId),
            requestedQuantity: item.requestedQuantity,
            reason: item.reason
          }))
        })
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Error al crear transferencia');
      }

      const result = await response.json();

      // Refrescar transferencias en background
      const transfersData = await fetchTransfers(transferFilters);
      setTransfers(transfersData);

      return { success: true, transfer: result.data };

    } catch (err: any) {
      return { success: false, error: err.message || 'Error al crear transferencia manual' };
    }
  }, [fetchTransfers, transferFilters]);

  return {
    // Data
    suggestions,
    transfers,
    loading,
    initialLoading,
    error,
    transferFilters,

    // Actions
    refreshData,
    generateSuggestions,
    createDistributionOrder,
    createManualTransfer,
    approveTransfer,
    shipTransfer,
    receiveTransfer,
    updateTransferFilters,
    searchTransfers
  };
};
